import type {CSSProperties, ReactNode} from 'react'
import {cn} from '@/lib/utils'

type MutedTextAlign = 'left' | 'center' | 'right'
type MutedTextWeight = 'normal' | 'medium' | 'semibold' | 'bold'

export interface MutedTextProps {
    children: ReactNode
    size?: 'xs' | 'sm' | 'base'
    block?: boolean
    centered?: boolean
    flex?: boolean
    align?: MutedTextAlign
    weight?: MutedTextWeight
    truncate?: boolean
    /** Render as a small-caps section label: uppercase letters with wider tracking. */
    uppercase?: boolean
    className?: string
    style?: CSSProperties
    'data-testid'?: string
    title?: string
}

const SIZE_CLASS: Record<'xs' | 'sm' | 'base', string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
}

const ALIGN_CLASS: Record<MutedTextAlign, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
}

const WEIGHT_CLASS: Record<MutedTextWeight, string> = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
}

export function MutedText({children, size = 'sm', block, centered, flex, align, weight, truncate, uppercase, className, style, 'data-testid': testId, title}: MutedTextProps) {
    return (
        <span
            className={cn(
                'text-muted-foreground',
                SIZE_CLASS[size],
                block && 'block',
                centered && 'text-center',
                flex && 'inline-flex items-center gap-1',
                align && ALIGN_CLASS[align],
                weight && WEIGHT_CLASS[weight],
                truncate && 'truncate',
                uppercase && 'uppercase tracking-wider',
                className,
            )}
            style={style}
            data-testid={testId}
            title={title}
        >
            {children}
        </span>
    )
}
